/**
 * SessionExpiredModal — A안 (`mockups/error-session-A.html`) 세션 만료 모달.
 *
 * GDEV-271 (238i). 2 variant 지원:
 *  - 'warning' (만료 임박): 카운트다운 + 연장/로그아웃 액션
 *  - 'expired' (이미 만료): 재로그인 안내
 *
 * 트리거: FE 타이머가 Access Token 만료 N분 전 감지 / refresh 실패.
 * 연장 동작: `POST /api/v1/auth/refresh` 성공 시 onExtend 호출.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useEffect, useState } from 'react'

interface SessionExpiredModalProps {
  open: boolean
  variant?: 'warning' | 'expired'
  /** 'warning' variant 에서 남은 초 — 0 도달 시 onExpire 호출 */
  remainingSeconds?: number
  onExtend?: () => void | Promise<void>
  onLogout?: () => void | Promise<void>
  onExpire?: () => void
}

function formatMmSs(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

export function SessionExpiredModal({
  open,
  variant = 'expired',
  remainingSeconds = 180,
  onExtend,
  onLogout,
  onExpire,
}: SessionExpiredModalProps) {
  const [remaining, setRemaining] = useState(remainingSeconds)

  // warning variant: 1초 단위 카운트다운
  useEffect(() => {
    if (!open || variant !== 'warning') return
    setRemaining(remainingSeconds)
    const id = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(id)
          onExpire?.()
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(id)
  }, [open, variant, remainingSeconds, onExpire])

  const isWarning = variant === 'warning'

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-[460px]">
        <DialogHeader>
          <span
            className={`mb-2 inline-flex w-fit rounded px-2 py-1 text-[11px] font-bold ${
              isWarning ? 'bg-audit-soft text-audit-fg' : 'bg-failed-soft text-failed-fg'
            }`}
          >
            {isWarning ? '⚠ 세션 만료 임박' : '🔒 세션 만료'}
          </span>
          <DialogTitle className="text-[18px] font-extrabold">
            {isWarning ? '로그인 세션이 곧 만료돼요' : '로그인 세션이 만료되었어요'}
          </DialogTitle>
          <DialogDescription className="m-0 mt-1 text-[13px] leading-relaxed text-muted-foreground">
            {isWarning ? (
              <>
                보안을 위해 30분 동안 활동이 없으면 자동 로그아웃됩니다.
                <br />
                세션을 연장하시겠어요?
              </>
            ) : (
              <>
                안전한 사용을 위해 자동 로그아웃되었습니다.
                <br />
                계속하려면 다시 로그인해 주세요.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isWarning && (
          <div className="rounded-md border border-border-base bg-muted/40 p-4 text-center">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              자동 로그아웃까지
            </div>
            <div
              className="mono mt-1 text-[36px] font-extrabold leading-none text-failed-fg"
              aria-live="polite"
              aria-label={`남은 시간 ${remaining}초`}
            >
              {formatMmSs(remaining)}
            </div>
            <div className="mt-1 text-[10px] text-muted-foreground">
              시간이 지나면 자동으로 로그아웃됩니다
            </div>
          </div>
        )}

        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          {isWarning ? (
            <>
              <button
                type="button"
                onClick={() => {
                  void onLogout?.()
                }}
                className="btn btn-outline text-[13px]"
              >
                로그아웃
              </button>
              <button
                type="button"
                onClick={() => {
                  void onExtend?.()
                }}
                className="btn btn-primary text-[13px]"
              >
                세션 연장
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                void onLogout?.()
              }}
              className="btn btn-primary text-[13px]"
            >
              다시 로그인
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
